import { CurrentUserContextType } from '@qualio/ui-components/lib/hooks/current-user/current-user';
import {
  BalloonToolbar,
  BlockToolbar,
  Bookmark,
  CloudServices,
  Editor,
  FindAndReplace,
  FontFamily,
  FontSize,
  Fullscreen,
  MultiRootEditor,
} from 'ckeditor5';
import {
  DocumentOutline,
  ImportWord,
  LineHeight,
  NarrowSidebar,
  Pagination,
  PresenceList,
  RealTimeCollaborativeRevisionHistory,
  RevisionHistory,
  TableOfContents,
  TrackChangesData,
  TrackChangesPreview,
  WideSidebar,
} from 'ckeditor5-premium-features';
import {
  DocumentFormat,
  DocumentUser,
  QualioDocument,
} from '../api/model/document';
import { Tag } from '../api/model/tag';
import { MedtechUserV2 } from '../api/user';
import { BalloonToolbarWorkaround } from '../plugins/BalloonToolbar/BalloonToolbarWorkaround';
import { MultirootFullscreen } from '../plugins/FullscreenAdapter/MultirootFullscreenAdapter';
import { QualioImportWordPlugin } from '../plugins/ImportWord/QualioImportWordPlugin';
import { QualioAttachmentPreviewPlugin } from '../plugins/Link/QualioAttachmentPreviewPlugin';
import { DocumentOutlineSorter } from '../plugins/OutlineSorter/DocumentOutlineSorter';
import { QualioPaginationTogglePlugin } from '../plugins/Pagination/QualioPaginationTogglePlugin';
import { exportPdfConfig } from '../plugins/PdfExport/QualioPdfExport';
import { QualioTableOfContentsCommand } from '../plugins/TableOfContents/QualioTableOfContentsCommand';
import { QualioTableOfContentsPlugin } from '../plugins/TableOfContents/QualioTableOfContentsPlugin';
import { qualioSuggestionThreadBuilder } from '../views/ckeditor/QualioSuggestionThreadView';
import { canUserEditDocument } from '../views/components/DocumentOverview/RenderLogic';
import { PresenceListConfig, SidebarConfig } from './DocumentEditorConfig';
import { ContextConfigProperties } from './DocumentEditorConfig.types';
import { DocumentEditorConfigFactory } from './DocumentEditorConfigFactory';
import {
  getContentEditorPlugins,
  getEditorAiPlugins,
  getMentionPluginConfig,
} from './Plugins';
import { CONTENT_EDITOR_TOOLBAR_ITEMS } from './ToolbarItems';

export type MultiRootConfigProperties = {
  collaborationEntityId: string;
  collaborationEntityName: string;
  documentId: number;
  documentStatusId: string;
  documentOwner: DocumentUser;
  companyId: number;
  handleAutoSave: (editor: Editor) => Promise<void>;
  commentsPermission: number;
  entityType: string;
  editorAiEnabled?: boolean;
  qualioDocument: QualioDocument;
  users?: MedtechUserV2[];
  tags?: Tag[];
  sidebarConfig: SidebarConfig;
  presenceListConfig: PresenceListConfig;
  editorContainerRef: React.RefObject<HTMLDivElement>;
  editorRevisionHistoryRef: React.RefObject<HTMLDivElement>;
  editorRevisionHistoryEditorRef: React.RefObject<HTMLDivElement>;
  docRevisionHistorySidebar: HTMLDivElement;
  editorInstanceRef: React.RefObject<MultiRootEditor>;
  editorToolbarRef: HTMLDivElement;
  additionalPremiumPlugins: boolean;
  usePagination?: boolean;
  currentUser: CurrentUserContextType;
  attachmentPreview?: (attachmentId: string) => void;
  useRevisionHistory?: boolean;
  useQualioTableOfContentsPlugin?: boolean;
  importFromWord?: boolean;
  onOpenReusableContentModal?: any;
} & ContextConfigProperties;

const buildAdditionalToolbarItemsForMultiroot = (
  multiRootConfigProperties: MultiRootConfigProperties,
) => {
  const isNativeDoc =
    multiRootConfigProperties.qualioDocument.document_format ===
    DocumentFormat.QualioNativeDocument;
  const canEditDocument = canUserEditDocument(
    multiRootConfigProperties.qualioDocument,
    multiRootConfigProperties.currentUser,
  );

  const multiRootToolbarItems = ['fullscreen'];

  if (
    canEditDocument &&
    isNativeDoc &&
    multiRootConfigProperties.useQualioTableOfContentsPlugin
  ) {
    multiRootToolbarItems.push(QualioTableOfContentsCommand.commandName);
  }

  if (multiRootConfigProperties.editorAiEnabled) {
    multiRootToolbarItems.push('aiCommands');
  }

  multiRootToolbarItems.push('undo', 'redo', '|');

  if (canEditDocument) {
    multiRootToolbarItems.push('trackChanges');
  }

  if (multiRootConfigProperties.useRevisionHistory) {
    multiRootToolbarItems.push('revisionHistory');
  }
  if (multiRootConfigProperties.importFromWord) {
    multiRootToolbarItems.push('importWord');
  }

  multiRootToolbarItems.push('findAndReplace', 'bookmark', '|');

  return multiRootToolbarItems;
};

export const getEditorConfigMultiRoot = (
  multiRootConfigProperties: MultiRootConfigProperties,
) => {
  let configFactory = new DocumentEditorConfigFactory();
  const contentEditorPlugins = getContentEditorPlugins();
  const reusableContentEnabled =
    multiRootConfigProperties.entityType !== 'change_control';
  let plugins = contentEditorPlugins.concat([
    CloudServices,
    PresenceList,
    NarrowSidebar,
    WideSidebar,
    TrackChangesData,
    TrackChangesPreview,
    FindAndReplace,
    FontFamily,
    FontSize,
    DocumentOutlineSorter,
    TableOfContents,
    Bookmark,
    DocumentOutline,
    LineHeight,
    MultirootFullscreen,
    Fullscreen,
    QualioAttachmentPreviewPlugin,
    ...(reusableContentEnabled ? [CKReusableContent.ReusableContent] : []),
    BlockToolbar,
    BalloonToolbar,
    BalloonToolbarWorkaround,
  ]);

  if (multiRootConfigProperties.useRevisionHistory) {
    plugins = plugins.concat([
      RevisionHistory,
      RealTimeCollaborativeRevisionHistory,
    ]);
  }

  if (multiRootConfigProperties.usePagination) {
    plugins = plugins.concat([Pagination, QualioPaginationTogglePlugin]);
  }

  if (multiRootConfigProperties.useQualioTableOfContentsPlugin) {
    plugins = plugins.concat([QualioTableOfContentsPlugin]);
  }

  if (multiRootConfigProperties.importFromWord) {
    plugins = plugins.concat([ImportWord, QualioImportWordPlugin]);
  }

  if (multiRootConfigProperties.editorAiEnabled) {
    plugins = getEditorAiPlugins().concat(plugins);
  }

  configFactory = configFactory
    .withCompanyId(multiRootConfigProperties.companyId)
    .withPlugins(plugins)
    .withAutosave((editor: Editor): Promise<void> => {
      // Must return a promise which resolves upon successful save
      return multiRootConfigProperties.handleAutoSave(editor);
    })
    .withComments(
      multiRootConfigProperties.commentsPermission,
      multiRootConfigProperties.qualioDocument,
      multiRootConfigProperties.users,
      multiRootConfigProperties.tags,
      true,
    )
    .withCollaboration(
      `company-${multiRootConfigProperties.companyId}-${multiRootConfigProperties.collaborationEntityName}-${multiRootConfigProperties.collaborationEntityId}`,
    )
    .withTrackChanges(
      qualioSuggestionThreadBuilder(
        multiRootConfigProperties.qualioDocument,
      ) as any,
    )
    .withPDFExport(exportPdfConfig)
    .withMention(getMentionPluginConfig(multiRootConfigProperties.companyId))
    .withDocumentId(multiRootConfigProperties.documentId)
    .withEntityType(multiRootConfigProperties.entityType)
    .withLists()
    .withMediaEmbed()
    .withCloudService(
      multiRootConfigProperties.collaborationToken,
      multiRootConfigProperties.editorBundleVersion,
    )
    .withSidebar(multiRootConfigProperties.sidebarConfig)
    .withPresenceList(multiRootConfigProperties.presenceListConfig)
    .withEditableParentSelector()
    .withPagination(multiRootConfigProperties.qualioDocument.orientation)
    .withNativeTableCaptionElement()
    .withFontFamily(true)
    .withFontSize(true);

  if (reusableContentEnabled) {
    configFactory = configFactory.withReusableContent(
      multiRootConfigProperties.companyId,
      multiRootConfigProperties.documentStatusId,
    );
  }

  configFactory = configFactory.withBlockToolbar();

  if (multiRootConfigProperties.useRevisionHistory) {
    configFactory = configFactory.withRevisionHistory(
      multiRootConfigProperties.editorContainerRef,
      multiRootConfigProperties.editorRevisionHistoryRef,
      multiRootConfigProperties.editorRevisionHistoryEditorRef,
      multiRootConfigProperties.docRevisionHistorySidebar,
      multiRootConfigProperties.editorInstanceRef,
      multiRootConfigProperties.editorToolbarRef,
    );
  }

  if (multiRootConfigProperties.importFromWord) {
    configFactory = configFactory.withImportFromWord();
  }

  if (multiRootConfigProperties.attachmentPreview) {
    configFactory = configFactory.withAttachmentPreview(
      multiRootConfigProperties.attachmentPreview,
    );
  }

  if (multiRootConfigProperties.onOpenReusableContentModal) {
    configFactory = configFactory.withOnOpenReusableContentModal(
      multiRootConfigProperties.onOpenReusableContentModal,
    );
  }

  if (multiRootConfigProperties.editorAiEnabled) {
    configFactory = configFactory.withAI();
  }

  const multiRootToolbarItems = buildAdditionalToolbarItemsForMultiroot(
    multiRootConfigProperties,
  );

  configFactory.withToolbarItems(
    multiRootToolbarItems.concat(
      CONTENT_EDITOR_TOOLBAR_ITEMS.slice(2),
      ...(multiRootConfigProperties.usePagination
        ? [QualioPaginationTogglePlugin.toolbarButtonName]
        : []),
      ...(multiRootConfigProperties.onOpenReusableContentModal
        ? [CKReusableContent.ReusableContent.toolbarButtonName]
        : []),
    ),
    true,
  );

  return configFactory.getConfig();
};
